import { ReactNode } from "react";

/**
 * La scatola del tooltip dei grafici della dashboard.
 *
 * Nasce dal tooltip dell'andamento pratiche in Panoramica — l'unico che era gia'
 * scritto a mano invece di usare il default di recharts — ed e' stata estratta qui
 * perche' tutti i grafici in giro raccontino il dato allo stesso modo: una riga di
 * contesto in alto (il giorno, il mese, il nome della fetta), poi una riga per serie
 * con il pallino del suo colore, l'etichetta a sinistra e il valore in grassetto a
 * destra, e una nota piccola in fondo quando c'e' qualcosa da aggiungere.
 *
 * Il default di recharts non sa niente di tutto questo: non ha il colore della serie
 * accanto al valore, non allinea i numeri e non distingue contesto da misura.
 */

export interface ChartTooltipRow {
  /** Cosa si sta misurando: "Pagato", "Margine", "Totale". */
  label: string;
  /** Gia' formattato da chi lo passa: valuta, conteggio, percentuale. */
  value: string;
  /** Colore della serie nel grafico: senza, la riga non ha pallino. */
  color?: string;
}

interface ChartTooltipCardProps {
  /** La riga di contesto in alto: la data, il mese, il nome della fetta. */
  title?: ReactNode;
  rows: ChartTooltipRow[];
  /** Nota in fondo, in piccolo: la quota sul totale, il delta del giorno. */
  footer?: ReactNode;
  /** Classe del footer, per i casi in cui il colore dice qualcosa (verde = incassato). */
  footerClassName?: string;
}

export function ChartTooltipCard({
  title,
  rows,
  footer,
  footerClassName = "text-gray-500",
}: ChartTooltipCardProps) {
  return (
    <div className="pointer-events-none min-w-[10rem] max-w-[16rem] rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm space-y-1">
      {title != null && (
        <p className="truncate text-gray-500" title={typeof title === "string" ? title : undefined}>
          {title}
        </p>
      )}
      {/* Indice nella chiave: due righe possono avere la stessa etichetta (due rate
          dello stesso cliente con lo stesso nome nel tooltip della Panoramica). */}
      {rows.map((row, i) => (
        <div key={`${row.label}-${i}`} className="flex items-center gap-3">
          {row.color && (
            <span
              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: row.color }}
            />
          )}
          <span className="truncate text-gray-600">{row.label}</span>
          <span className="ml-auto flex-shrink-0 font-semibold tabular-nums text-gray-900">
            {row.value}
          </span>
        </div>
      ))}
      {footer != null && <p className={`text-xs ${footerClassName}`}>{footer}</p>}
    </div>
  );
}

/**
 * La quota della fetta sul totale, arrotondata a una cifra: nei donut il valore da
 * solo non dice quanto pesa, e contare le fette a occhio non e' un'informazione.
 */
export function formatShare(value: number, total: number): string {
  if (!total) return "0";
  return ((value / total) * 100).toLocaleString("it-IT", {
    maximumFractionDigits: 1,
  });
}

/** Il cursore grigio chiaro sotto la colonna puntata: quello di default e' troppo scuro. */
export const CHART_TOOLTIP_CURSOR = { fill: "rgba(148, 163, 184, 0.12)" };

export default ChartTooltipCard;
